import dayjs from "dayjs";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import { MoreOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { Row, Col, Input, Table } from "antd";
import { StringParam, NumberParam, withDefault, useQueryParams } from "use-query-params";
import { Empty, Modal, Button, Popover, Dropdown, Typography, ConfigProvider } from "antd";

import { BatchNIBSS } from "components/Status";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { openNotificationWithIcon } from "utils";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { BatchSummaryInput, BatchSummaryDTO } from "models";
import { useSearchSummaryBatchQuery } from "pages/Generation/generationApiSlice";
import { useRePushSingleBatchToNIBSSMutation } from "pages/Reprocess/reprocessApiSlice";
import {
  updateLayout,
  setIsReprocessSingleBatchToNIBSSModalOpen,
} from "pages/Reprocess/reprocessSlice";

const { Search } = Input;

const BatchTableContainer = styled.div`
  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const ReprocessBatchTable: React.FC<any> = () => {
  const now = dayjs();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [batchId, setBactchId] = useState("");
  const { isReprocessSingleBatchToNIBSSModalOpen } = useAppSelector((state) => state.reprocess);

  const [batchSummaryParams, setBatchSummaryParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    batchId: withDefault(StringParam, ""),
    statuses: withDefault(StringParam, "EXTRACTED"),
    nibbsStatus: withDefault(StringParam, "BAD_REQUEST"),
    drBatchStatus: withDefault(StringParam, "EXTRACTED"),
    crBatchStatus: withDefault(StringParam, "EXTRACTED"),
    responseMessage: withDefault(
      StringParam,
      "One or more Instrument(s) does/do not have an account set up for this transaction!",
    ),
    endExtractionDate: withDefault(StringParam, now.endOf("day").format("YYYY-MM-DD")),
    // startExtractionDate: withDefault(StringParam, now.startOf("day").format("YYYY-MM-DD")),
    startExtractionDate: withDefault(StringParam, now.subtract(1, "months").format("YYYY-MM-DD")),
  });

  const [rePushSingleBatchToNIBSS, rePushSingleBatchToNIBSSResult] =
    useRePushSingleBatchToNIBSSMutation();

  const summaryBatch = useSearchSummaryBatchQuery(batchSummaryParams as any);

  const handleFilterChange = (
    name: string = "",
    value: {
      label?: string;
      value?: string;
      key?: string;
      disabled?: boolean;
    },
  ) => {
    setBatchSummaryParams({
      page: 0,
      [name]: value.value,
    });
  };

  const handleTableChange = async (pagination: any, filters: any, sorter: any) => {
    setBatchSummaryParams({
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  const handleRePushSingleBatchToNIBSS = (batchId: string) => {
    rePushSingleBatchToNIBSS({ batchId })
      .unwrap()
      .then((payload) => {
        dispatch(setIsReprocessSingleBatchToNIBSSModalOpen(false));
        openNotificationWithIcon(`successfully`, payload.response, "success");
      })
      .catch((error) => {
        dispatch(setIsReprocessSingleBatchToNIBSSModalOpen(false));
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          error.data?.errorMessageList?.length
            ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
                openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      });
  };

  useEffect(() => {
    dispatch(
      updateLayout({
        title: "Re-Process - Transaction Batch",
        function: "HOME",
        path: "",
      }),
    );
  }, [dispatch]);

  const columns: ColumnsType<BatchSummaryInput> = [
    {
      title: "Batch ID",
      dataIndex: "batchID",
      key: "batchID",
      width: "150px",
    },

    {
      title: "Total Debit",
      dataIndex: "computedTotalDebit",
      key: "computedTotalDebit",
      width: "120px",
      align: "right",
    },

    {
      title: "Total Credit",
      dataIndex: "computedTotalCredit",
      key: "computedTotalCredit",
      width: "120px",
      align: "right",
    },

    {
      title: "Extraction Date",
      dataIndex: "extractionTime",
      key: "extractionTime",
      width: "18%",
      align: "right",
      render: (text: string, record) => dayjs(record.extractionTime).format("YYYY-MM-DD HH:mm A"),
    },

    {
      title: "POS TXN Date",
      dataIndex: "posTransactionDate",
      key: "posTransactionDate",
      width: "12%",
      align: "center",
    },

    {
      title: "Response",
      dataIndex: "responseCode",
      key: "responseCode",
      width: "70px",
      align: "center",
      render: (_: unknown, record: BatchSummaryDTO) =>
        record.responseMessage ? (
          <Popover
            placement="left"
            overlayStyle={{
              fontSize: "14px",
              maxWidth: "200px",
              textTransform: "lowercase",
            }}
            overlayInnerStyle={{
              fontSize: "13px",
              maxWidth: "200px",
              textTransform: "lowercase",
            }}
            title={record.responseCode}
            content={record.responseMessage}
          >
            <span style={{ cursor: "pointer" }}> {record.responseCode} </span>
          </Popover>
        ) : (
          record.responseCode
        ),
    },

    {
      title: "NIBSS Status",
      dataIndex: "nibbsStatus",
      key: "nibssStatus",
      width: "125px",
      align: "center",
      render: (value) => <BatchNIBSS status={value} />,
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "center",
      width: "80px",

      render: (text: string, record) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: 0,
                  label: (
                    <Button
                      type="link"
                      style={{ width: "100%", textAlign: "left" }}
                      onClick={() => navigate(`/reprocess/edit/${record.batchID}`)}
                    >
                      Edit
                    </Button>
                  ),
                },
                {
                  key: 1,
                  label: (
                    <Button
                      type="link"
                      style={{ width: "100%", textAlign: "left" }}
                      onClick={() => {
                        dispatch(setIsReprocessSingleBatchToNIBSSModalOpen(true));
                        setBactchId(record.batchID);
                      }}
                    >
                      Re-Push
                    </Button>
                  ),
                },
              ],
            }}
          >
            <MoreOutlined
              style={{
                margin: 0,
                padding: 0,
                fontSize: "22px",
                cursor: "pointer",
                color: "#1890ff",
                background: "transparent",
              }}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <BatchTableContainer>
      <Helmet>
        <title>NEFT Settlement | Batch</title>
        <meta name="description" content="Helmet application" />
      </Helmet>
      <Row justify="space-between" style={{ padding: "15px 0px" }}>
        <Col>
          <Button
            size="large"
            shape="circle"
            onClick={() => navigate(-1)}
            icon={<ArrowLeftOutlined />}
          />
        </Col>

        <Col>
          <Search
            allowClear
            enterButton
            size="large"
            addonBefore="BatchID"
            style={{ width: "auto" }}
            placeholder="Enter Batch ID"
            onSearch={(value: string) => handleFilterChange("batchId", { value })}
            defaultValue={batchSummaryParams?.batchId ? batchSummaryParams?.batchId : ""}
          />
        </Col>
      </Row>

      <ConfigProvider
        renderEmpty={() =>
          summaryBatch.isError ? (
            <ApiErrorTableComponent queryResult={summaryBatch} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
      >
        <Table
          key="uuid"
          scroll={{ x: 0 }}
          columns={columns as any}
          onChange={handleTableChange}
          loading={summaryBatch.isFetching}
          rowKey={(record: BatchSummaryDTO) => record?.id || ""}
          dataSource={summaryBatch.isError ? [] : summaryBatch.data?.response?.content}
          pagination={{
            pageSizeOptions: [5, 10, 20, 50, 100],
            pageSize: summaryBatch.data?.response?.size,
            total: summaryBatch.data?.response?.totalElements,
            showTotal: (total: any, range: any[]) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </ConfigProvider>

      <Modal
        okText="Yes"
        maskClosable={false}
        title="Push Batch To NIBSS"
        visible={isReprocessSingleBatchToNIBSSModalOpen}
        onOk={() => handleRePushSingleBatchToNIBSS(batchId)}
        closable={!rePushSingleBatchToNIBSSResult.isLoading}
        confirmLoading={rePushSingleBatchToNIBSSResult.isLoading}
        onCancel={() => dispatch(setIsReprocessSingleBatchToNIBSSModalOpen(false))}
        cancelButtonProps={{
          disabled: rePushSingleBatchToNIBSSResult.isLoading,
        }}
      >
        <Typography.Text style={{ fontSize: "14px" }}>
          Are you sure you want to re-push batch with ID of {batchId} to NIBSS
        </Typography.Text>
      </Modal>
    </BatchTableContainer>
  );
};

export default ReprocessBatchTable;
